import React, { forwardRef } from 'react'
import { useNavigate, useLocation, useSearchParams as useRRSearchParams, useParams as useRRParams, Link as RRLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export interface Session {
  user?: {
    name?: string | null
    email?: string | null
    image?: string | null
  }
  accessToken?: string
  accessTokenExpired?: number
  expires: string
}

// Helper to localize URLs
const languages = ['fr', 'en', 'pt', 'es', 'it', 'ar']
const localizeHref = (href: string, currentLang?: string) => {
  if (!href || typeof href !== 'string' || !currentLang) return href
  if (href.startsWith('http') || href.startsWith('#')) return href

  // Check if it already has a language prefix
  const pathParts = href.split('/')
  if (pathParts[1] && (languages as readonly string[]).includes(pathParts[1])) {
    return href
  }

  // Prepend current language
  const normalizedHref = href.startsWith('/') ? href : `/${href}`
  return `/${currentLang}${normalizedHref}`
}

// Link Shim
interface LinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href?: string
  children?: React.ReactNode
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(({ href, children, ...props }, ref) => {
  const params = useRRParams<{ lang: string }>()
  const { i18n } = useTranslation()
  const lang = params.lang || i18n.language

  // Handle external links or anchors
  if (!href || (typeof href === 'string' && (href.startsWith('http') || href.startsWith('#')))) {
    return (
      <a href={href} ref={ref} {...props}>
        {children}
      </a>
    )
  }

  const localizedHref = localizeHref(href, lang)
  return (
    <RRLink to={localizedHref} ref={ref} {...props}>
      {children}
    </RRLink>
  )
})

// Image Shim
interface StaticImageData {
  src?: string
  default?: string | { src?: string }
}

interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'ref'> {
  src?: string | StaticImageData
  alt?: string
  width?: number | string
  height?: number | string
  priority?: boolean
  unoptimized?: boolean
  ref?: React.Ref<HTMLImageElement>
}

export const Image = ({ src, alt, width, height, priority, unoptimized, className, style, ref, ...props }: ImageProps) => {
  // Handle Next.js static imports which are objects { src, height, width } or Vite default exports
  let imageSrc: string | undefined
  if (typeof src === 'string') {
    imageSrc = src
  } else if (typeof src === 'object' && src !== null) {
    const defaultVal = src.default
    imageSrc =
      src.src ||
      (typeof defaultVal === 'object' && defaultVal !== null ? defaultVal.src : undefined) ||
      (typeof defaultVal === 'string' ? defaultVal : undefined)
  }

  // Mimic Next.js responsive behavior if width/height are provided
  const responsiveStyle =
    width && height
      ? {
          maxWidth: '100%',
        }
      : {}

  return (
    <img
      src={imageSrc}
      alt={alt || ''}
      width={width}
      height={height}
      className={className}
      style={{ ...responsiveStyle, ...style }}
      loading={priority ? 'eager' : 'lazy'}
      ref={ref}
      {...props}
    />
  )
}

// useRouter Shim
export const useRouter = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { lang: paramLang } = useRRParams<{ lang: string }>()
  const { i18n } = useTranslation()
  const lang = paramLang || i18n.language

  return {
    push: (href: string) => navigate(localizeHref(href, lang)),
    replace: (href: string) => navigate(localizeHref(href, lang), { replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
    refresh: () => window.location.reload(),
    pathname: location.pathname,
    query: Object.fromEntries(new URLSearchParams(location.search)),
  }
}

// usePathname Shim
export const usePathname = () => {
  const location = useLocation()
  const { lang: paramLang } = useRRParams<{ lang: string }>()
  const { i18n } = useTranslation()
  const lang = paramLang || i18n.language

  // Remove lang prefix for compatibility with next-intl usePathname
  if (lang && location.pathname.startsWith(`/${lang}`)) {
    return location.pathname.substring(lang.length + 1) || '/'
  }
  return location.pathname
}

// useSearchParams Shim
export const useSearchParams = () => {
  const [searchParams] = useRRSearchParams()
  return searchParams
}

// useParams Shim
export const useParams = () => {
  return useRRParams()
}
